import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import { useKnowledge } from '../hooks/useKnowledge';
import { usePageMeta } from '../hooks/usePageMeta';

export default function Resources() {
  usePageMeta({
    title: 'Resources: Dive into the Library | OVERALL WELLBEING',
    description:
      'Clear, practical guides without the jargon. Search and browse the full OVERALL WELLBEING library by area of life, and see which topics belong to the Essentials.',
    path: '/resources',
    image: '/og-resources.jpg',
  });

  const { foundations, domains, topics, loading } = useKnowledge();
  const [progress, setProgress] = useState(0);
  const [query, setQuery] = useState('');
  const [activeDomain, setActiveDomain] = useState('all');

  useEffect(() => {
    const onScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const foundationName = useMemo(() => {
    const m = {};
    foundations.forEach((f) => { m[f.code] = f.name; });
    return m;
  }, [foundations]);

  const domainName = useMemo(() => {
    const m = {};
    domains.forEach((d) => { m[d.code] = d.name; });
    return m;
  }, [domains]);

  const filteredTopics = useMemo(() => {
    const q = query.trim().toLowerCase();
    return topics
      .filter((t) => {
        const matchDomain = activeDomain === 'all' || t.domain_code === activeDomain;
        const matchQ = !q || `${t.title} ${t.domain} ${t.canonical_id}`.toLowerCase().includes(q);
        return matchDomain && matchQ;
      })
      .sort((a, b) => a.number - b.number);
  }, [topics, query, activeDomain]);

  return (
    <div className="flex flex-col w-full">
      {/* Progress bar */}
      <div className="sticky top-20 left-0 right-0 z-40 w-full h-1 bg-surface-container overflow-hidden">
        <div className="h-full bg-surface-tint transition-all duration-150" style={{ width: `${progress}%` }} />
      </div>

      {/* Breadcrumbs */}
      <section className="w-full px-margin-mobile lg:px-margin pt-space-xl pb-space-lg">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-space-md">
          <div className="flex items-center flex-wrap gap-space-xs text-on-surface-variant font-label-md text-label-md uppercase tracking-wider">
            <Link to="/wellbeing" className="hover:text-surface-tint transition-colors">EXPLORE WELLBEING</Link>
            <span className="text-surface-container-highest">/</span>
            <span className="text-outline font-extrabold">THE LIBRARY</span>
          </div>
          <div className="flex items-center gap-space-sm bg-primary-container px-space-md py-space-xs rounded-full">
            <span className="w-2 h-2 rounded-full bg-surface-tint" />
            <span className="font-label-sm text-label-sm uppercase tracking-widest text-outline">Essentials • Areas of Life • Practical Guides</span>
          </div>
        </div>
      </section>

      {/* HERO + SEARCH */}
      <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-gutter items-end">
          <div className="lg:col-span-7 flex flex-col">
            <div className="inline-flex flex-wrap items-center gap-space-xs mb-space-md">
              <span className="font-label-md text-label-md tracking-widest uppercase bg-surface-container px-space-sm py-space-xs rounded text-outline font-bold">KNOWLEDGE LIBRARY</span>
              <span className="text-on-surface-variant font-label-md uppercase tracking-wider">Updated as our guides grow</span>
            </div>
            <h1 className="font-display-hero text-[40px] leading-[42px] md:text-[56px] md:leading-[56px] lg:text-display-hero text-outline tracking-tight uppercase mb-space-lg">
              THE WELLBEING <br />
              <span className="text-surface-tint">LIBRARY</span>
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface leading-relaxed max-w-2xl">
              Clear, practical guides without the jargon. Browse every topic in the OVERALL WELLBEING library, grouped by area of life and cross-linked to the Essentials.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="w-full bg-surface-container-low p-space-md rounded-lg shadow-sm">
              <div className="relative w-full">
                <Icon name="search" className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant text-[22px]" />
                <input
                  data-testid="resource-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 bg-surface text-on-surface placeholder:text-on-surface-variant font-body-md text-body-md rounded-lg focus:outline-none focus:ring-2 focus:ring-surface-tint transition-all"
                  placeholder="Search the library..."
                  type="text"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DOMAIN FILTER */}
      <section className="w-full px-margin-mobile lg:px-margin pb-space-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center gap-space-xs">
            <button
              data-testid="resource-domain-filter-all"
              onClick={() => setActiveDomain('all')}
              className={`h-9 px-space-md rounded-full font-label-md text-label-md uppercase tracking-wider transition-all ${activeDomain === 'all' ? 'bg-surface-tint text-on-primary' : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container'}`}
            >
              All Topics
            </button>
            {domains.map((d) => (
              <button
                key={d.code}
                data-testid={`resource-domain-filter-${d.code}`}
                onClick={() => setActiveDomain(d.code)}
                title={d.name}
                className={`h-9 px-space-md rounded-full font-label-md text-label-md uppercase tracking-wider transition-all ${activeDomain === d.code ? 'bg-surface-tint text-on-primary' : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container'}`}
              >
                {d.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* TOPIC INDEX */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-lg bg-surface">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-space-xl pb-space-md border-b border-surface-container">
            <div>
              <span className="font-label-md text-label-md text-surface-tint uppercase tracking-widest font-bold block mb-space-xs">
                {activeDomain === 'all' ? 'BROWSE EVERYTHING' : (domainName[activeDomain] || '')}
              </span>
              <h2 className="font-headline-md text-headline-md text-outline uppercase tracking-tight">Dive into the Library</h2>
            </div>
            <span data-testid="resource-topic-count" className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant mt-space-sm md:mt-0">
              {filteredTopics.length} topic{filteredTopics.length === 1 ? '' : 's'} shown
            </span>
          </div>

          {loading && (
            <div data-testid="resource-loading" className="py-space-2xl flex items-center gap-space-sm text-on-surface-variant">
              <span className="inline-block w-2 h-2 rounded-full bg-surface-tint animate-pulse" />
              <span className="font-label-md text-label-md uppercase tracking-wider">Loading the library…</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-space-md">
            {filteredTopics.map((t) => (
              <Link
                to={`/topics/${t.slug}`}
                key={t.canonical_id}
                data-testid={`topic-card-${t.canonical_id}`}
                className="p-space-lg bg-surface-container-low rounded-lg hover:bg-primary-container transition-colors group flex items-start justify-between shadow-sm relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-1 h-full bg-surface-tint opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="pr-space-sm">
                  <h4 className="font-title-md text-title-md text-outline font-bold group-hover:text-surface-tint transition-colors">{t.title}</h4>
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">{t.domain}</p>
                  {(t.foundation_codes || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-space-sm">
                      {t.foundation_codes.map((fc) => (
                        <span key={fc} className="px-2 py-0.5 rounded-full bg-surface text-outline font-label-sm text-label-sm uppercase">
                          <Icon name="star" className="text-[11px] text-surface-tint align-middle mr-0.5" />{foundationName[fc] || fc}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {!loading && filteredTopics.length === 0 && (
            <div className="py-space-3xl text-center" data-testid="resource-empty">
              <p className="font-headline-sm text-headline-sm text-outline uppercase">No topics found</p>
              <p className="font-body-md text-body-md text-on-surface-variant mt-space-xs">Try a different search term or area of life.</p>
            </div>
          )}
        </div>
      </section>

      {/* FOUNDATIONS OVERLAY LEGEND */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-2xl bg-surface-container-low">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-gutter items-start">
          <div className="lg:col-span-4 bg-surface p-space-xl rounded-lg shadow-sm">
            <h3 className="font-headline-sm text-headline-sm text-outline font-bold uppercase mb-space-md">The Essentials</h3>
            <p className="font-body-md text-body-md text-on-surface-variant mb-space-lg">The Essentials are the building blocks of energy, focus and healthy ageing. Topics carrying a <Icon name="star" className="text-[13px] text-surface-tint align-middle" /> marker are part of one or more Essentials.</p>
            <Link to="/longevity" className="inline-flex items-center gap-space-xs font-title-md text-title-md text-surface-tint font-bold hover:text-primary transition-colors">
              <span>Start with the Essentials</span>
              <Icon name="arrow_forward" className="text-[20px]" />
            </Link>
          </div>
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-space-md">
            {foundations.map((f) => (
              <Link key={f.code} to={`/essentials/${f.slug}`} data-testid={`essential-chip-${f.slug}`} className="p-space-md bg-surface rounded-lg shadow-sm flex flex-col justify-between hover:bg-primary-container transition-colors group">
                <span className="font-label-sm text-label-sm uppercase tracking-wider text-surface-tint font-bold">Essential {String(f.order).padStart(2, '0')}</span>
                <h4 className="font-body-md text-body-md text-outline font-semibold mt-1 leading-snug group-hover:text-surface-tint transition-colors">{f.name}</h4>
                <span className="font-label-sm text-label-sm uppercase text-on-surface-variant mt-space-sm">{(f.mapped_slugs || f.mapped_topic_ids || []).length} guide{(f.mapped_slugs || f.mapped_topic_ids || []).length === 1 ? '' : 's'}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SAFETY NOTICE */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-xl bg-surface">
        <div className="max-w-5xl mx-auto p-space-xl rounded-lg bg-primary-container shadow-sm">
          <div className="flex items-start gap-space-md">
            <div className="w-10 h-10 rounded-full bg-surface-tint text-on-primary flex-shrink-0 flex items-center justify-center"><Icon name="health_and_safety" className="text-[22px]" /></div>
            <div className="flex flex-col">
              <h4 className="font-title-md text-title-md uppercase tracking-wider text-outline font-bold mb-space-xs">Evidence &amp; Responsible-Use Notice</h4>
              <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed mb-space-sm">
                The OVERALL WELLBEING library is educational and is not a substitute for personalised medical advice. Our topics summarise general, evidence-informed guidance. If you have a specific health condition, please speak with a qualified health professional before making significant lifestyle changes.
              </p>
              <span className="font-label-sm text-label-sm uppercase tracking-widest text-on-surface-variant font-bold">EDUCATIONAL GUIDANCE • NOT MEDICAL ADVICE</span>
            </div>
          </div>
        </div>
      </section>

      {/* ACTION BAR */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-3xl bg-surface-container">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
          <span className="font-label-md text-label-md text-surface-tint uppercase tracking-widest font-bold mb-space-xs">START WHERE IT MATTERS</span>
          <h2 className="font-headline-lg text-headline-lg text-outline uppercase tracking-tight mb-space-md">Turn Knowledge Into Rhythm</h2>
          <p className="font-body-lg text-body-lg text-on-surface-variant max-w-xl mb-space-2xl">Explore the guides, then build a calm personal practice inside your private wellbeing space.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-space-md w-full sm:w-auto">
            <Link to="/wellbeing" className="w-full sm:w-auto px-space-xl h-12 rounded bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-widest font-bold shadow-[0_8px_24px_rgba(240,40,223,0.30)] hover:bg-primary transition-all flex items-center justify-center gap-space-xs">
              <Icon name="grid_view" className="text-[20px]" />
              <span>Explore Areas of Life</span>
            </Link>
            <Link to="/my-login" className="w-full sm:w-auto px-space-xl h-12 rounded bg-outline text-surface font-label-md text-label-md uppercase tracking-widest font-bold hover:bg-on-surface transition-all flex items-center justify-center gap-space-xs">
              <Icon name="person" className="text-[20px]" />
              <span>Open My Wellbeing Space</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
